// src/pages/OutlookSyncPage.js
import React, { useState, useEffect, useCallback } from 'react';
import { useApp } from '../contexts/AppContext';
import { collection, addDoc, serverTimestamp, getDocs, query, where, deleteDoc, doc, writeBatch } from 'firebase/firestore';
import { db } from '../config/firebase';

const VERLOF_TYPES = [
  { value: 'verlof', label: 'Vakantieverlof', color: '#4CAF50' },
  { value: 'bijzonder', label: 'Bijzonder verlof', color: '#FF9800' },
  { value: 'ziekte', label: 'Ziekteverlof', color: '#F44336' },
  { value: 'zwangerschap', label: 'Zwangerschapsverlof', color: '#E91E63' },
  { value: 'zorg', label: 'Zorgverlof', color: '#9C27B0' },
  { value: 'overuren', label: 'Overuren opnemen', color: '#607D8B' },
  { value: 'feestdag', label: 'Feestdag', color: '#795548' },
  { value: 'onbekend', label: 'Overig', color: '#9E9E9E' },
];

const MAILBOXES = [
  { value: 'info@slaaploods.nl',     label: 'info@slaaploods.nl' },
  { value: 'planning@slaaploods.nl', label: 'planning@slaaploods.nl' },
  { value: 'beide',                  label: "Beide agenda\u2019s" },
];

const PRIMARY = '#1976D2';

function fmt(iso) {
  if (!iso) return '-';
  try { return new Date(iso).toLocaleDateString('nl-NL', { day: '2-digit', month: '2-digit', year: 'numeric' }); }
  catch { return iso; }
}
function fmtTijd(iso) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' }); }
  catch { return ''; }
}
// Bereken uren uit event. Niet-hele-dag: verschil in uren. Hele-dag: NaN (werkrooster nodig).
function eventUren(ev) {
  if (!ev.start || !ev.end) return null;
  if (ev.isAllDay) return null; // moet via werkrooster
  const ms = new Date(ev.end) - new Date(ev.start);
  return Math.round((ms / 3600000) * 100) / 100; // uren, 2 decimalen
}
// Aantal werkdagen (ma-vr) tussen twee datums (voor hele-dag events)
function werkdagen(startStr, endStr) {
  if (!startStr || !endStr) return 1;
  const s = new Date(startStr); const e = new Date(endStr);
  let count = 0;
  const d = new Date(s);
  while (d < e) {
    const dag = d.getDay();
    if (dag !== 0 && dag !== 6) count++;
    d.setDate(d.getDate() + 1);
  }
  return Math.max(1, count);
}
// Label voor de uren-kolom
function urenLabel(ev, med) {
  const u = eventUren(ev);
  if (u !== null) return u + 'u';
  // Hele dag: bereken met werkrooster als medewerker geselecteerd
  const wd = werkdagen(ev.start, ev.end);
  if (med) {
    const dagUren = (med.ContractUren || med.contractUren || 0) / 5;
    if (dagUren > 0) return (wd * dagUren) + 'u';
  }
  return wd + (wd === 1 ? ' dag' : ' dgn');
}
// Bereken import-uren (voor opslaan)
function importUren(ev, med) {
  const u = eventUren(ev);
  if (u !== null) return u;
  const wd = werkdagen(ev.start, ev.end);
  const dagUren = (med?.ContractUren || med?.contractUren || 0) / 5;
  return dagUren > 0 ? wd * dagUren : wd * 8; // fallback 8u/dag
}

const S = {
  card: { background: '#fff', borderRadius: 8, boxShadow: '0 1px 4px rgba(0,0,0,0.1)', padding: 16, marginBottom: 16 },
  input: { padding: '6px 10px', borderRadius: 6, border: '1px solid #ddd', fontSize: 13 },
  select: { padding: '4px 8px', borderRadius: 6, border: '1px solid #ddd', fontSize: 12, width: '100%' },
  tag: (color) => ({ background: color + '22', color, padding: '2px 10px', borderRadius: 12, fontWeight: 600, fontSize: 12, whiteSpace: 'nowrap' }),
};

function Btn({ children, onClick, disabled, variant = 'primary', small }) {
  const colors = {
    primary: { bg: PRIMARY, color: '#fff', border: 'none' },
    success: { bg: '#4CAF50', color: '#fff', border: 'none' },
    outline: { bg: 'transparent', color: PRIMARY, border: '1px solid ' + PRIMARY },
    danger:  { bg: 'transparent', color: '#F44336', border: '1px solid #F44336' },
  };
  const c = colors[variant] || colors.primary;
  return (
    <button onClick={onClick} disabled={disabled} style={{
      padding: small ? '4px 12px' : '8px 18px', borderRadius: 6,
      background: c.bg, color: c.color, border: c.border,
      cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.6 : 1,
      fontSize: small ? 12 : 14, fontWeight: 500, whiteSpace: 'nowrap',
    }}>{children}</button>
  );
}

export default function OutlookSyncPage() {
  const { medewerkers } = useApp();

  // Filter & mailbox state
  const [mailbox, setMailbox]       = useState('info@slaaploods.nl');
  const [filterText, setFilterText] = useState('');
  const [dateRange, setDateRange]   = useState({
    start: new Date(Date.now() - 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    end:   new Date().toISOString().split('T')[0],
  });

  // Events state
  const [tab, setTab]               = useState('import');
  const [loading, setLoading]       = useState(false);
  const [allEvents, setAllEvents]   = useState([]);
  const [selected, setSelected]     = useState({});
  const [mapping, setMapping]       = useState({});
  const [importing, setImporting]   = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [error, setError]           = useState('');
  const [syncStatus, setSyncStatus] = useState(null);
  const [syncLoading, setSyncLoading] = useState(false);
  const [progress, setProgress]     = useState(0);
  const [confirm, setConfirm]       = useState(false);
  const [imported, setImported]     = useState(new Set());
  const [sortCol, setSortCol]       = useState('start');  // default: op datum
  const [sortDir, setSortDir]       = useState('asc');    // asc of desc
  const [cleanupRunning, setCleanupRunning] = useState(false);
  const [cleanupResult, setCleanupResult]   = useState(null);
  const [cleanupConfirm, setCleanupConfirm] = useState(false);

  const actief = (medewerkers || []).filter(m => m.actief !== false);

  // Sorteer-functie
  const toggleSort = (col) => {
    if (sortCol === col) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortCol(col);
      setSortDir('asc');
    }
  };

  // Gefilterde events op basis van filterText (live filtering)
  // Komma = OF tussen groepen, spatie = EN binnen een groep
  // Bijv: "vrij Corina, vrij Erwin" → (vrij EN Corina) OF (vrij EN Erwin)
  const parseFilter = (text) => {
    if (!text.trim()) return [];
    return text.split(',').map(group =>
      group.trim().toLowerCase().split(/\s+/).filter(Boolean)
    ).filter(g => g.length > 0);
  };
  const filterGroups = parseFilter(filterText);

  const filtered = filterGroups.length > 0
    ? allEvents.filter(e => {
        const onderwerp = (e.subject || '').toLowerCase();
        // Minstens één groep moet volledig matchen (OR tussen groepen)
        return filterGroups.some(woorden =>
          // Alle woorden in de groep moeten aanwezig zijn (AND binnen groep)
          woorden.every(w => onderwerp.includes(w))
        );
      })
    : allEvents;

  // Sorteer gefilterde events
  const filteredEvents = [...filtered].sort((a, b) => {
    let va, vb;
    switch (sortCol) {
      case 'subject':  va = (a.subject || '').toLowerCase(); vb = (b.subject || '').toLowerCase(); break;
      case 'mailbox':  va = (a._mailbox || ''); vb = (b._mailbox || ''); break;
      case 'start':    va = a.start || ''; vb = b.start || ''; break;
      case 'end':      va = a.end || ''; vb = b.end || ''; break;
      case 'uren':     va = eventUren(a) ?? werkdagen(a.start, a.end) * 8; vb = eventUren(b) ?? werkdagen(b.start, b.end) * 8; break;
      case 'status':   va = imported.has(a.id) ? 1 : 0; vb = imported.has(b.id) ? 1 : 0; break;
      default:         va = a.start || ''; vb = b.start || '';
    }
    if (va < vb) return sortDir === 'asc' ? -1 : 1;
    if (va > vb) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  const importable = filteredEvents.filter(e => !imported.has(e.id));
  const selEvents  = filteredEvents.filter(e => selected[e.id]);

  // Haal reeds geimporteerde event-ids op uit Firestore
  useEffect(() => {
    (async () => {
      try {
        const snap = await getDocs(query(collection(db, 'leaveRequests'), where('bronGraphEventId', '!=', null)));
        setImported(new Set(snap.docs.map(d => d.data().bronGraphEventId).filter(Boolean)));
      } catch (e) { /* stille fout */ }
    })();
  }, []);

  // Haal events op — alles server-side (incl. 'beide' + dedup + paginatie)
  const fetchEvents = useCallback(async () => {
    setLoading(true); setError(''); setAllEvents([]); setSelected({}); setMapping({});
    try {
      const url = `/api/graph/events?startDate=${dateRange.start}&endDate=${dateRange.end}&mailbox=${encodeURIComponent(mailbox)}`;
      const r = await fetch(url);
      if (!r.ok) { const e = await r.json(); throw new Error(e.message || 'HTTP ' + r.status); }
      const data = await r.json();
      const events = data.events || [];
      setAllEvents(events);
      const m = {};
      events.forEach(e => {
        if (!imported.has(e.id)) m[e.id] = { type: e.suggestedType || 'onbekend', employeeId: '', urenOverride: '' };
      });
      setMapping(m);
    } catch (err) { setError('Fout: ' + err.message); }
    finally { setLoading(false); }
  }, [dateRange, mailbox, imported]);

  const fetchSync = useCallback(async () => {
    setSyncLoading(true);
    try { const r = await fetch('/api/graph/sync'); setSyncStatus(await r.json()); }
    catch (err) { setError('Sync fout: ' + err.message); }
    finally { setSyncLoading(false); }
  }, []);

  useEffect(() => { if (tab === 'sync') fetchSync(); }, [tab, fetchSync]);

  const toggleAll = () => {
    const all = importable.length > 0 && importable.every(e => selected[e.id]);
    const next = {};
    importable.forEach(e => { next[e.id] = !all; });
    setSelected(next);
  };

  const doImport = async () => {
    setConfirm(false); setImporting(true); setProgress(0);
    let ok = 0, fail = 0, errs = [];
    for (let i = 0; i < selEvents.length; i++) {
      const ev = selEvents[i];
      const m = mapping[ev.id] || {};
      if (!m.employeeId) { fail++; errs.push(ev.subject + ': geen medewerker'); continue; }
      const med = actief.find(x => x.id === m.employeeId);
      if (!med) { fail++; errs.push(ev.subject + ': niet gevonden'); continue; }

      // Gebruik handmatige uren als ingevuld, anders bereken
      const uren = m.urenOverride !== '' && m.urenOverride !== undefined
        ? parseFloat(m.urenOverride)
        : importUren(ev, med);

      // Bepaal jaar uit startdatum
      const jaar = ev.start ? new Date(ev.start).getFullYear() : new Date().getFullYear();

      // Detecteer zaterdag
      const startDate = new Date(ev.start);
      const isZat = startDate.getDay() === 6;

      if (isZat && m.type !== 'feestdag') {
        // Zaterdag → boek als overuren (plus)
        try {
          await addDoc(collection(db, 'overuren'), {
            medewerkerId: m.employeeId,
            medewerkerNaam: med.Naam || med.naam,
            datum: ev.start.split('T')[0],
            uren: uren,
            status: 'goedgekeurd',
            opmerking: ev.subject + (ev.preview ? ' — ' + ev.preview : ''),
            bronOutlook: true,
            bronGraphEventId: ev.id,
            bronMailbox: ev._mailbox || mailbox,
            createdAt: serverTimestamp(),
          });
          ok++;
        } catch (e) { fail++; errs.push(ev.subject + ' (overuren): ' + e.message); }
      } else {
        // Doordeweeks/zondag → verlofaanvraag met correcte DB-veldnamen
        try {
          await addDoc(collection(db, 'leaveRequests'), {
            userId: m.employeeId,
            userName: med.Naam || med.naam,
            submittedBy: 'outlook-import',
            submittedByName: 'Outlook Import',
            type: m.type || 'verlof',
            startDate: ev.start,
            endDate: ev.end,
            uren: uren,
            reason: ev.subject + (ev.preview ? ' — ' + ev.preview : ''),
            status: 'approved',
            jaar: jaar,
            gedeeltelijk: !ev.isAllDay,
            beginTijd: !ev.isAllDay ? fmtTijd(ev.start) : null,
            eindTijd: !ev.isAllDay ? fmtTijd(ev.end) : null,
            isOpenZiek: false,
            gebruikOveruren: false,
            overurenGebruikt: 0,
            bronOutlook: true,
            bronGraphEventId: ev.id,
            bronMailbox: ev._mailbox || mailbox,
            createdAt: serverTimestamp(),
          });
          ok++;
        } catch (e) { fail++; errs.push(ev.subject + ': ' + e.message); }
      }
      setProgress(Math.round(((i + 1) / selEvents.length) * 100));
    }
    setImporting(false); setImportResult({ ok, fail, errs }); setSelected({});
    const snap = await getDocs(query(collection(db, 'leaveRequests'), where('bronGraphEventId', '!=', null)));
    setImported(new Set(snap.docs.map(d => d.data().bronGraphEventId).filter(Boolean)));
  };

  const subscribe = async () => {
    setSyncLoading(true);
    try {
      const r = await fetch('/api/graph/sync', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'subscribe' }),
      });
      if (!r.ok) throw new Error((await r.json()).message);
      fetchSync();
    } catch (e) { setError('Activeren mislukt: ' + e.message); }
    finally { setSyncLoading(false); }
  };

  const unsubscribe = async (id) => {
    setSyncLoading(true);
    try {
      await fetch('/api/graph/sync', {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ subscriptionId: id }),
      });
      fetchSync();
    } catch (e) { setError(e.message); }
    finally { setSyncLoading(false); }
  };

  // === DATABASE CLEANUP ===
  const doCleanup = async () => {
    setCleanupConfirm(false);
    setCleanupRunning(true);
    setCleanupResult(null);
    const results = {};
    const errors = [];
    const collections = ['leaveRequests', 'verlofAanvragen', 'overuren', 'flexUitbetalingen', 'flexUren', 'flexPlanning', 'roosterWijzigingen', 'jaararchief'];
    for (const colName of collections) {
      try {
        const snap = await getDocs(collection(db, colName));
        let deleted = 0;
        const total = snap.docs.length;
        // Delete per document (robuuster dan writeBatch bij security rules)
        for (const d of snap.docs) {
          try {
            await deleteDoc(doc(db, colName, d.id));
            deleted++;
          } catch (docErr) {
            errors.push(`${colName}/${d.id}: ${docErr.message}`);
          }
        }
        results[colName] = { deleted, total };
      } catch (colErr) {
        results[colName] = { deleted: 0, total: '?', error: colErr.message };
        errors.push(`${colName}: ${colErr.message}`);
      }
    }
    setCleanupResult({ success: errors.length === 0, results, errors });
    setImported(new Set());
    setAllEvents([]);
    setCleanupRunning(false);
  };

  // Mailbox badge kleur
  const mbColor = (mb) => mb?.includes('planning') ? '#9C27B0' : '#1976D2';

  // Highlight zoekwoorden in onderwerp
  const highlight = (text) => {
    if (filterGroups.length === 0) return text;
    // Alle unieke woorden uit alle groepen
    const allWords = [...new Set(filterGroups.flat())];
    const regex = new RegExp(`(${allWords.map(w => w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})`, 'gi');
    const parts = text.split(regex);
    return parts.map((part, i) =>
      allWords.some(w => part.toLowerCase() === w)
        ? <mark key={i} style={{ background: '#FFF176', borderRadius: 2, padding: '0 2px' }}>{part}</mark>
        : part
    );
  };

  return (
    <div style={{ padding: 24, maxWidth: 1400, margin: '0 auto' }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
        <span style={{ fontSize: 28 }}>&#128197;</span>
        <div>
          <div style={{ fontSize: 22, fontWeight: 700 }}>Outlook Agenda Koppeling</div>
          <div style={{ fontSize: 13, color: '#666' }}>Microsoft Graph API &#8594; Slaaploods.nl</div>
        </div>
        <div style={{ marginLeft: 'auto', background: '#E8F5E9', color: '#2E7D32', padding: '4px 12px', borderRadius: 16, fontSize: 13, fontWeight: 600 }}>
          &#10003; Verbonden
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        {[['import', '\uD83D\uDCE5 Historische import'], ['sync', '\uD83D\uDD04 Live sync'], ['cleanup', '\u26A0\uFE0F Database reset']].map(([k, l]) => (
          <Btn key={k} onClick={() => setTab(k)} variant={tab === k ? (k === 'cleanup' ? 'danger' : 'primary') : 'outline'}>{l}</Btn>
        ))}
      </div>

      {error && (
        <div style={{ background: '#FFEBEE', color: '#c62828', padding: '10px 16px', borderRadius: 6, marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>{error}</span>
          <button onClick={() => setError('')} style={{ background: 'none', border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 18, lineHeight: 1 }}>&times;</button>
        </div>
      )}

      {/* === IMPORT TAB === */}
      {tab === 'import' && (
        <div>
          {/* Filter balk */}
          <div style={{ ...S.card, display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#555' }}>Agenda</label>
              <select value={mailbox} onChange={e => setMailbox(e.target.value)}
                style={{ ...S.input, minWidth: 220 }}>
                {MAILBOXES.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
              </select>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#555' }}>Van</label>
              <input type="date" value={dateRange.start}
                onChange={e => setDateRange(p => ({ ...p, start: e.target.value }))}
                style={S.input} />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#555' }}>Tot</label>
              <input type="date" value={dateRange.end}
                onChange={e => setDateRange(p => ({ ...p, end: e.target.value }))}
                style={S.input} />
            </div>
            <div style={{ paddingBottom: 2 }}>
              <Btn onClick={fetchEvents} disabled={loading}>
                {loading ? '\u23F3 Laden...' : '\uD83D\uDD0D Events ophalen'}
              </Btn>
            </div>
            {allEvents.length > 0 && (
              <div style={{ fontSize: 13, color: '#666', alignSelf: 'flex-end', paddingBottom: 4 }}>
                <strong>{allEvents.length}</strong> events geladen
              </div>
            )}
          </div>

          {/* Zoekfilter op onderwerp */}
          {allEvents.length > 0 && (
            <div style={{ ...S.card, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: '#555', whiteSpace: 'nowrap' }}>{'\uD83D\uDD0E'} Filter op onderwerp:</span>
              <input
                type="text"
                placeholder="bijv: vrij Corina, vrij Erwin (komma = of)"
                value={filterText}
                onChange={e => { setFilterText(e.target.value); setSelected({}); }}
                style={{ ...S.input, minWidth: 280, flex: 1 }}
              />
              {filterText && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  {filterGroups.map((groep, gi) => (
                    <span key={gi} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      {gi > 0 && <span style={{ fontSize: 11, color: '#999', fontWeight: 700 }}>OF</span>}
                      {groep.map(w => (
                        <span key={w} style={{ background: '#1976D218', color: PRIMARY, border: '1px solid #1976D244', borderRadius: 12, padding: '2px 10px', fontSize: 12, fontWeight: 600 }}>{w}</span>
                      ))}
                    </span>
                  ))}
                  <span style={{ fontSize: 13, color: PRIMARY, fontWeight: 600 }}>
                    {filteredEvents.length} van {allEvents.length}
                  </span>
                  <Btn onClick={() => { setFilterText(''); setSelected({}); }} variant="outline" small>&times; Wis</Btn>
                </div>
              )}
            </div>
          )}

          {/* Import resultaat */}
          {importResult && (
            <div style={{ background: importResult.fail > 0 ? '#FFF3E0' : '#E8F5E9', padding: '12px 16px', borderRadius: 6, marginBottom: 16 }}>
              <strong>{importResult.ok} events ge&#239;mporteerd</strong>
              {importResult.fail > 0 && <span> &middot; {importResult.fail} mislukt</span>}
              {importResult.errs.map((e, i) => <div key={i} style={{ fontSize: 12, color: '#666', marginTop: 4 }}>&#8226; {e}</div>)}
              <div style={{ marginTop: 8 }}><Btn onClick={() => setImportResult(null)} variant="outline" small>Sluiten</Btn></div>
            </div>
          )}

          {/* Voortgangsbalk */}
          {importing && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 13, marginBottom: 4 }}>Importeren... {progress}%</div>
              <div style={{ background: '#e0e0e0', borderRadius: 4, height: 6 }}>
                <div style={{ background: PRIMARY, width: progress + '%', height: 6, borderRadius: 4, transition: 'width 0.3s' }} />
              </div>
            </div>
          )}

          {/* Events tabel */}
          {filteredEvents.length > 0 && (
            <div style={{ ...S.card, padding: 0, overflow: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: '#F5F5F5', position: 'sticky', top: 0 }}>
                    <th style={{ padding: '10px 12px', width: 40 }}>
                      <input type="checkbox"
                        checked={importable.length > 0 && importable.every(e => selected[e.id])}
                        onChange={toggleAll} />
                    </th>
                    {[
                      { label: 'Onderwerp', key: 'subject' },
                      { label: 'Agenda',    key: 'mailbox' },
                      { label: 'Van',       key: 'start' },
                      { label: 'Tot',       key: 'end' },
                      { label: 'Uren',      key: 'uren' },
                      { label: 'Type verlof', key: null },
                      { label: 'Medewerker',  key: null },
                      { label: 'Status',    key: 'status' },
                    ].map(col => (
                      <th key={col.label}
                        onClick={col.key ? () => toggleSort(col.key) : undefined}
                        style={{
                          padding: '10px 12px', textAlign: 'left', fontWeight: 600,
                          whiteSpace: 'nowrap',
                          cursor: col.key ? 'pointer' : 'default',
                          userSelect: 'none',
                          color: sortCol === col.key ? PRIMARY : undefined,
                        }}>
                        {col.label}
                        {col.key && sortCol === col.key && (
                          <span style={{ marginLeft: 4, fontSize: 11 }}>
                            {sortDir === 'asc' ? '\u25B2' : '\u25BC'}
                          </span>
                        )}
                        {col.key && sortCol !== col.key && (
                          <span style={{ marginLeft: 4, fontSize: 11, opacity: 0.3 }}>{'\u25B2'}</span>
                        )}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredEvents.map(ev => {
                    const isImp = imported.has(ev.id);
                    const m = mapping[ev.id] || {};
                    const typeInfo = VERLOF_TYPES.find(t => t.value === m.type);
                    const mbClr = mbColor(ev._mailbox);
                    return (
                      <tr key={ev.id} style={{
                        opacity: isImp ? 0.5 : 1,
                        background: selected[ev.id] ? '#E3F2FD' : 'white',
                        borderBottom: '1px solid #f0f0f0',
                      }}>
                        <td style={{ padding: '8px 12px' }}>
                          <input type="checkbox" checked={!!selected[ev.id]}
                            onChange={() => setSelected(p => ({ ...p, [ev.id]: !p[ev.id] }))}
                            disabled={isImp} />
                        </td>
                        <td style={{ padding: '8px 12px', maxWidth: 220 }}>
                          <div title={ev.preview} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {highlight(ev.subject || '')}
                          </div>
                        </td>
                        <td style={{ padding: '8px 12px', whiteSpace: 'nowrap' }}>
                          <span style={{ ...S.tag(mbClr), fontSize: 11 }}>
                            {ev._mailbox?.replace('@slaaploods.nl', '') || 'info'}
                          </span>
                        </td>
                        <td style={{ padding: '8px 12px', whiteSpace: 'nowrap' }}>{fmt(ev.start)}{!ev.isAllDay && <span style={{ fontSize: 10, color: '#999', marginLeft: 4 }}>{fmtTijd(ev.start)}</span>}</td>
                        <td style={{ padding: '8px 12px', whiteSpace: 'nowrap' }}>{fmt(ev.end)}{!ev.isAllDay && <span style={{ fontSize: 10, color: '#999', marginLeft: 4 }}>{fmtTijd(ev.end)}</span>}</td>
                        <td style={{ padding: '8px 12px', textAlign: 'center', fontWeight: 600, color: ev.isAllDay ? '#666' : PRIMARY }}>
                          {isImp ? (
                            urenLabel(ev, null)
                          ) : (
                            <input
                              type="number"
                              step="0.5"
                              min="0"
                              value={m.urenOverride !== '' ? m.urenOverride : importUren(ev, actief.find(x => x.id === m.employeeId))}
                              onChange={e => setMapping(p => ({ ...p, [ev.id]: { ...(p[ev.id] || {}), urenOverride: e.target.value } }))}
                              style={{ width: 60, padding: '3px 6px', borderRadius: 4, border: m.urenOverride !== '' ? '2px solid #FF9800' : '1px solid #ddd', fontSize: 13, textAlign: 'center', fontWeight: 600, color: m.urenOverride !== '' ? '#E65100' : PRIMARY, background: m.urenOverride !== '' ? '#FFF3E0' : 'transparent' }}
                              title={m.urenOverride !== '' ? 'Handmatig aangepast' : 'Berekend: ' + urenLabel(ev, actief.find(x => x.id === m.employeeId))}
                            />
                          )}
                        </td>
                        <td style={{ padding: '8px 12px', minWidth: 170 }}>
                          {isImp ? (
                            <span style={S.tag(typeInfo?.color || '#999')}>{typeInfo?.label || m.type}</span>
                          ) : (
                            <select value={m.type || 'onbekend'}
                              onChange={e => setMapping(p => ({ ...p, [ev.id]: { ...(p[ev.id] || {}), type: e.target.value } }))}
                              style={S.select}>
                              {VERLOF_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                            </select>
                          )}
                        </td>
                        <td style={{ padding: '8px 12px', minWidth: 170 }}>
                          {isImp ? <span style={{ color: '#999' }}>-</span> : (
                            <select value={m.employeeId || ''}
                              onChange={e => setMapping(p => ({ ...p, [ev.id]: { ...(p[ev.id] || {}), employeeId: e.target.value } }))}
                              style={S.select}>
                              <option value="">Kies medewerker</option>
                              {actief.map(med => <option key={med.id} value={med.id}>{med.Naam || med.naam}</option>)}
                            </select>
                          )}
                        </td>
                        <td style={{ padding: '8px 12px' }}>
                          <span style={{ padding: '2px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600,
                            background: isImp ? '#E8F5E9' : '#E3F2FD', color: isImp ? '#2E7D32' : PRIMARY }}>
                            {isImp ? 'Ge\u00EFmporteerd' : 'Nieuw'}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Geselecteerde events actiebalk */}
          {selEvents.length > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginTop: 12 }}>
              <span style={{ fontSize: 14 }}><strong>{selEvents.length}</strong> events geselecteerd</span>
              <Btn onClick={() => setConfirm(true)} disabled={importing} variant="success">
                Importeer als verlofaanvragen
              </Btn>
            </div>
          )}

          {/* Lege staat */}
          {allEvents.length === 0 && !loading && (
            <div style={{ ...S.card, textAlign: 'center', padding: 48, color: '#aaa' }}>
              <div style={{ fontSize: 48, marginBottom: 8 }}>&#128197;</div>
              <div style={{ fontSize: 15 }}>Selecteer een agenda en klik op "Events ophalen"</div>
              <div style={{ fontSize: 13, marginTop: 4 }}>Je kunt info@, planning@ of beide agenda's tegelijk laden</div>
            </div>
          )}

          {/* Gefilterd maar geen resultaten */}
          {allEvents.length > 0 && filteredEvents.length === 0 && (
            <div style={{ ...S.card, textAlign: 'center', padding: 32, color: '#aaa' }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>&#128269;</div>
              <div>Geen events gevonden voor "<strong>{filterText}</strong>"</div>
              <div style={{ marginTop: 12 }}><Btn onClick={() => { setFilterText(''); setSelected({}); }} variant="outline" small>Wis filter</Btn></div>
            </div>
          )}

          {/* Bevestigingsdialog */}
          {confirm && (
            <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
              <div style={{ background: '#fff', borderRadius: 10, padding: 28, maxWidth: 460, width: '90%', boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }}>
                <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 12 }}>Importeren bevestigen</div>
                <div style={{ fontSize: 14, lineHeight: 1.6, marginBottom: 16 }}>
                  Je importeert <strong>{selEvents.length}</strong> agenda-events als verlofaanvragen met status <strong>goedgekeurd</strong>.
                  {selEvents.some(e => !mapping[e.id]?.employeeId) && (
                    <div style={{ background: '#FFF3E0', color: '#E65100', padding: '8px 12px', borderRadius: 6, marginTop: 10, fontSize: 13 }}>
                      &#9888; Events zonder medewerker worden overgeslagen.
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                  <Btn onClick={() => setConfirm(false)} variant="outline">Annuleren</Btn>
                  <Btn onClick={doImport} variant="success">Importeer {selEvents.length} events</Btn>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* === SYNC TAB === */}
      {tab === 'sync' && (
        <div style={S.card}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
            <span style={{ fontSize: 20 }}>{'\uD83D\uDD04'}</span>
            <span style={{ fontSize: 17, fontWeight: 600 }}>Live synchronisatie</span>
            <div style={{ marginLeft: 'auto' }}>
              <Btn onClick={fetchSync} disabled={syncLoading} variant="outline" small>Vernieuwen</Btn>
            </div>
          </div>
          <div style={{ background: '#E3F2FD', color: '#1565C0', padding: '10px 16px', borderRadius: 6, fontSize: 13, marginBottom: 16 }}>
            Graph subscriptions verlopen na maximaal 3 dagen. Vernieuw periodiek.
          </div>
          {syncLoading && <div style={{ color: '#999', fontSize: 13, marginBottom: 12 }}>Laden...</div>}
          {syncStatus && (
            <>
              <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 8 }}>Actieve subscriptions</div>
              {(syncStatus.subscriptions || []).length === 0 ? (
                <div style={{ color: '#999', fontSize: 13, marginBottom: 16 }}>Geen actieve subscriptions.</div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, marginBottom: 16 }}>
                  <thead><tr style={{ background: '#F5F5F5' }}>
                    {['Resource', 'Verloopt op', 'Actie'].map(h => (
                      <th key={h} style={{ padding: '8px 12px', textAlign: 'left' }}>{h}</th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {syncStatus.subscriptions.map(sub => (
                      <tr key={sub.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                        <td style={{ padding: '8px 12px' }}>{sub.resource}</td>
                        <td style={{ padding: '8px 12px' }}>{fmt(sub.expirationDateTime)}</td>
                        <td style={{ padding: '8px 12px' }}>
                          <Btn onClick={() => unsubscribe(sub.id)} variant="danger" small>Deactiveer</Btn>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              <Btn onClick={subscribe} disabled={syncLoading}>Activeer live sync</Btn>
            </>
          )}
        </div>
      )}

      {/* === CLEANUP TAB === */}
      {tab === 'cleanup' && (
        <div>
          <div style={{ ...S.card, background: '#FFF3E0', border: '2px solid #E65100' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
              <span style={{ fontSize: 28 }}>{'\u26A0\uFE0F'}</span>
              <div>
                <div style={{ fontSize: 18, fontWeight: 700, color: '#BF360C' }}>Database reset</div>
                <div style={{ fontSize: 13, color: '#E65100' }}>Verwijder alle mutaties om schoon te importeren vanuit Outlook</div>
              </div>
            </div>
            <div style={{ fontSize: 13, lineHeight: 1.8, color: '#333', marginBottom: 16 }}>
              Dit verwijdert <strong>permanent</strong> alle documenten uit de volgende Firestore collections:
            </div>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
              {['leaveRequests', 'verlofAanvragen', 'overuren', 'flexUitbetalingen', 'flexUren', 'flexPlanning', 'roosterWijzigingen', 'jaararchief'].map(c => (
                <span key={c} style={{ background: '#FFCCBC', color: '#BF360C', padding: '6px 14px', borderRadius: 8, fontWeight: 700, fontSize: 13, fontFamily: 'monospace' }}>{c}</span>
              ))}
            </div>
            <div style={{ background: '#FBE9E7', borderRadius: 8, padding: '12px 16px', fontSize: 13, color: '#BF360C', marginBottom: 16, lineHeight: 1.6 }}>
              <strong>Wat blijft behouden:</strong> medewerkers, instellingen, verlofTypes, gebruikersaccounts, wachtwoorden, rollen.
            </div>
            <Btn onClick={() => setCleanupConfirm(true)} disabled={cleanupRunning} variant="danger">
              {cleanupRunning ? '\u23F3 Bezig met verwijderen...' : '\uD83D\uDDD1\uFE0F Start database reset'}
            </Btn>
          </div>

          {cleanupResult && (
            <div style={{ ...S.card, background: cleanupResult.success ? '#E8F5E9' : '#FFEBEE', border: `1px solid ${cleanupResult.success ? '#4CAF50' : '#F44336'}` }}>
              <div style={{ fontSize: 16, fontWeight: 700, color: cleanupResult.success ? '#2E7D32' : '#C62828', marginBottom: 8 }}>
                {cleanupResult.success ? '\u2705 Reset voltooid' : '\u274C Fout bij reset'}
              </div>
              {cleanupResult.success && Object.entries(cleanupResult.results).map(([col, info]) => (
                <div key={col} style={{ fontSize: 13, color: '#333', marginBottom: 4 }}>
                  <strong>{col}</strong>: {info.deleted} van {info.total} documenten verwijderd
                  {info.error && <span style={{ color: '#C62828' }}> — {info.error}</span>}
                </div>
              ))}
              {!cleanupResult.success && Object.entries(cleanupResult.results).map(([col, info]) => (
                <div key={col} style={{ fontSize: 13, color: '#333', marginBottom: 4 }}>
                  <strong>{col}</strong>: {info.deleted} van {info.total} verwijderd
                  {info.error && <span style={{ color: '#C62828' }}> — {info.error}</span>}
                </div>
              ))}
              {cleanupResult.errors?.length > 0 && (
                <div style={{ marginTop: 8, fontSize: 12, color: '#C62828', maxHeight: 120, overflow: 'auto' }}>
                  {cleanupResult.errors.map((e, i) => <div key={i}>{'\u2022'} {e}</div>)}
                </div>
              )}
              {cleanupResult.error && (
                <div style={{ fontSize: 13, color: '#C62828' }}>{cleanupResult.error}</div>
              )}
            </div>
          )}

          {/* Dubbele bevestigingsdialog */}
          {cleanupConfirm && (
            <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
              <div style={{ background: '#fff', borderRadius: 12, padding: 32, maxWidth: 500, width: '90%', boxShadow: '0 8px 32px rgba(0,0,0,0.3)' }}>
                <div style={{ fontSize: 40, textAlign: 'center', marginBottom: 12 }}>{'\uD83D\uDEA8'}</div>
                <div style={{ fontSize: 20, fontWeight: 700, color: '#C62828', textAlign: 'center', marginBottom: 16 }}>
                  Weet je het zeker?
                </div>
                <div style={{ fontSize: 14, color: '#333', lineHeight: 1.6, marginBottom: 8 }}>
                  Je staat op het punt om <strong>alle verlofaanvragen, overuren en uitbetalingen</strong> definitief te verwijderen.
                </div>
                <div style={{ background: '#FFEBEE', padding: '10px 14px', borderRadius: 8, fontSize: 13, color: '#C62828', marginBottom: 20 }}>
                  Deze actie kan <strong>niet</strong> ongedaan worden gemaakt!
                </div>
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                  <Btn onClick={() => setCleanupConfirm(false)} variant="outline">Annuleren</Btn>
                  <Btn onClick={doCleanup} variant="danger">Ja, verwijder alles</Btn>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

    </div>
  );
}
